/* ==============================================================================
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
============================================================================== */

import React, { useEffect, useState } from 'react';

// @mui material components
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDBadgeDot from 'components/MDBadgeDot';

// Material Dashboard 2 PRO React examples
import PieChart from 'examples/Charts/PieChart';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Footer from 'examples/Footer';
import ComplexStatisticsCard from 'examples/Cards/StatisticsCards/ComplexStatisticsCard';
import DefaultLineChart from 'examples/Charts/LineCharts/DefaultLineChart';
import VerticalBarChart from 'examples/Charts/BarCharts/VerticalBarChart';
import HorizontalBarChart from 'examples/Charts/BarCharts/HorizontalBarChart';
import DataTable from 'examples/Tables/DataTable';
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';
import { useSelector } from 'react-redux';

// Material Dashboard 2 PRO React examples
import Navbar from 'examples/Navbars/NavBar';
import ProductCell from 'layouts/dashboards/sales/components/ProductCell';
import RefundsCell from 'layouts/dashboards/sales/components/RefundsCell';
import DefaultCell from 'layouts/dashboards/sales/components/DefaultCell';

const initChart = {
  title: '',
  description: '',
  date: '',
  chart: {
    labels: [],
    datasets: [],
  },
};

function DashboardAccount() {
  const mod = 'dashboard_account';
  const { t, i18n } = useTranslation();
  const app = useSelector((state) => state[APP_STATE]);
  const [sale, setSale] = useState([]);
  const [chartPie, setChartPie] = useState(initChart);
  const [chartLine, setChartLine] = useState(initChart);
  const [chartVertical, setChartVertical] = useState(initChart);
  const [chartHorizontal, setChartHorizontal] = useState(initChart);
  const [stat, setStat] = useState({
    stat1: { count: '', color: 'white', amount: '', label: '' },
    stat2: { count: '', color: 'white', amount: '', label: '' },
    stat3: { count: '', color: 'white', amount: '', label: '' },
    stat4: { count: '', color: 'white', amount: '', label: '' },
    stat5: { count: '', color: 'white', amount: '', label: '' },
    stat6: { count: '', color: 'white', amount: '', label: '' },
  });

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setStat(result.stat);
        setChartPie(result.pie);
        setChartLine(result.line);
        setChartVertical(result.vertical);
        setChartHorizontal(result.horizontal);
        setSale(result.sale);
      } else {
        Func.exitApp();
      }
    });
  };

  const renderStat = () => {
    return (
      <MDBox>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={2}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color='dark'
                icon='paid'
                title='Bookings'
                count={stat.stat1.count}
                percentage={{
                  color: stat.stat1.color,
                  amount: stat.stat1.amount,
                  label: stat.stat1.label,
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={2}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color='warning'
                icon='weekend'
                title='Bookings'
                count={stat.stat2.count}
                percentage={{
                  color: stat.stat2.color,
                  amount: stat.stat2.amount,
                  label: stat.stat2.label,
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={2}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                icon='leaderboard'
                title='Users'
                count={stat.stat3.count}
                percentage={{
                  color: stat.stat3.color,
                  amount: stat.stat3.amount,
                  label: stat.stat3.label,
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={2}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color='success'
                icon='store'
                title='Revenue'
                count={stat.stat4.count}
                percentage={{
                  color: stat.stat4.color,
                  amount: stat.stat4.amount,
                  label: stat.stat4.label,
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={2}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color='primary'
                icon='person_add'
                title='Followers'
                count={stat.stat5.count}
                percentage={{
                  color: stat.stat5.color,
                  amount: stat.stat5.amount,
                  label: stat.stat5.label,
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={2}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color='error'
                icon='point_of_sale'
                title='Followers'
                count={stat.stat6.count}
                percentage={{
                  color: stat.stat6.color,
                  amount: stat.stat6.amount,
                  label: stat.stat6.label,
                }}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderChart = () => {
    return (
      <MDBox mb={3} mt={1}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} lg={4}>
            <Card sx={{ height: '100%' }}>
              <MDBox py={3} pr={2} pl={3}>
                <MDBox>
                  <MDTypography variant='h6'>Channels</MDTypography>
                  <MDBox mb={2}>
                    <MDTypography component='div' variant='button' color='text'>
                      Sales related to age average
                    </MDTypography>
                  </MDBox>
                </MDBox>
                <MDBox mt={3}>
                  <Grid container alignItems='center'>
                    <Grid item xs={7}>
                      <PieChart chart={chartPie.chart} height='12.5rem' />
                    </Grid>
                    <Grid item xs={5}>
                      <MDBox pr={1}>
                        <MDBox mb={1}>
                          <MDBadgeDot color='info' size='sm' badgeContent='Facebook' />
                        </MDBox>
                        <MDBox mb={1}>
                          <MDBadgeDot color='primary' size='sm' badgeContent='Direct' />
                        </MDBox>
                        <MDBox mb={1}>
                          <MDBadgeDot color='dark' size='sm' badgeContent='Organic' />
                        </MDBox>
                        <MDBox mb={1}>
                          <MDBadgeDot color='secondary' size='sm' badgeContent='Referral' />
                        </MDBox>
                      </MDBox>
                    </Grid>
                  </Grid>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} lg={8}>
            <DefaultLineChart
              title='Revenue'
              description={
                <MDBox display='flex' justifyContent='space-between'>
                  <MDBox display='flex' ml={-1}>
                    <MDBadgeDot color='info' size='sm' badgeContent='Facebook Ads' />
                    <MDBadgeDot color='dark' size='sm' badgeContent='Google Ads' />
                  </MDBox>
                </MDBox>
              }
              chart={chartLine.chart}
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderBar = () => {
    return (
      <MDBox mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}>
            <VerticalBarChart
              title='Bar chart'
              description='Sales related to age average'
              chart={chartVertical.chart}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <HorizontalBarChart
              title='Sales by age'
              description='Sales related to age average'
              chart={chartHorizontal.chart}
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderTable = () => {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <MDBox pt={3} px={3}>
              <MDTypography variant='h6' fontWeight='medium'>
                Top Selling Products
              </MDTypography>
            </MDBox>
            <MDBox py={1}>
              <DataTable
                table={{
                  columns: [
                    { Header: 'product', accessor: 'product', width: '55%' },
                    { Header: 'value', accessor: 'value' },
                    { Header: 'ads spent', accessor: 'adsSpent', align: 'center' },
                    { Header: 'refunds', accessor: 'refunds', align: 'center' },
                  ],

                  rows: sale.map((item) => {
                    return {
                      product: <ProductCell image={item.image} name={item.name} orders={item.id} />,
                      value: <DefaultCell>{item.price}</DefaultCell>,
                      adsSpent: <DefaultCell>{item.price}</DefaultCell>,
                      refunds:
                        item.id % 2 === 0 ? (
                          <RefundsCell
                            value={item.id}
                            icon={{ color: 'success', name: 'keyboard_arrow_up' }}
                          />
                        ) : (
                          <RefundsCell
                            value={40}
                            icon={{ color: 'error', name: 'keyboard_arrow_down' }}
                          />
                        ),
                    };
                  }),
                }}
                entriesPerPage={false}
                showTotalEntries={false}
                isSorted={false}
                noEndBorder
              />
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, []);

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.dashboard`), t(`menu.dashboard_account`)]} />
      <MDBox py={3}>
        {renderStat()}
        {renderChart()}
        {renderBar()}
        {renderTable()}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default DashboardAccount;
